import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BlockIcon from '@mui/icons-material/Block';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SearchIcon from '@mui/icons-material/Search';
import {
  noticesService,
  NoticeType,
  BulkAssignNoticesData,
  BulkAssignPreviewData,
} from '../../../services/notices-service';
import { getClientSettings } from '../../../services/clients-service';
import { useAuth } from '../../../contexts/AuthContext';
import {
  BulkPhoneDeliveryMethod,
  computeBillableUnits,
  formatBillableUnitsEstimate,
  MAX_BULK_PHONE_BILLABLE_UNITS,
  validateBulkBillableUnits,
  validateBulkDpCount,
} from '../../../utils/bulk-notice-limits';
import EffectiveDateSection, {
  dateInputToIsoStartOfDay,
  todayDateInputValue,
} from './EffectiveDateSection';
import { getApiErrorMessage } from '../../../utils/api-error';

export interface BulkAssignNoticeModalProps {
  open: boolean;
  onClose: () => void;
  dpIds: number[];
  onSuccess: () => void;
}

type NoticeTypeFilter = 'ALL' | 'BREACH_NOTICE' | 'ERASURE_NOTICE' | 'INFORMATIONAL_NOTICE';
type ModalStep = 'form' | 'results';

const parseEnabled = (value: unknown) =>
  value === 'true' || value === '1' || value === true || value === 'TRUE';

const noticeTypeLabel = (t: NoticeType) => t.replace('_NOTICE', '').replace('_', ' ');

const BulkAssignNoticeModal: React.FC<BulkAssignNoticeModalProps> = ({
  open,
  onClose,
  dpIds,
  onSuccess,
}) => {
  const theme = useTheme();
  const { user } = useAuth();

  const [step, setStep] = useState<ModalStep>('form');
  const [resolvedDpIds, setResolvedDpIds] = useState<number[]>([]);
  const [preview, setPreview] = useState<BulkAssignPreviewData | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const [notices, setNotices] = useState<
    { noticeId: number; noticeType: NoticeType; title: string; description?: string | null }[]
  >([]);
  const [loadingNotices, setLoadingNotices] = useState(false);
  const [noticesError, setNoticesError] = useState<string | null>(null);

  const [smsEnabled, setSmsEnabled] = useState(false);
  const [whatsAppEnabled, setWhatsAppEnabled] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<NoticeTypeFilter>('ALL');
  const [selectedNoticeIds, setSelectedNoticeIds] = useState<Set<number>>(new Set());
  const [deliveryMethod, setDeliveryMethod] = useState<BulkPhoneDeliveryMethod | null>(null);
  const [blockedExpanded, setBlockedExpanded] = useState(false);

  const [useCustomEffectiveDate, setUseCustomEffectiveDate] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState(() => todayDateInputValue());
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [bulkResult, setBulkResult] = useState<BulkAssignNoticesData | null>(null);

  const resetForm = () => {
    setStep('form');
    setPreview(null);
    setPreviewError(null);
    setNoticesError(null);
    setSearchTerm('');
    setTypeFilter('ALL');
    setSelectedNoticeIds(new Set());
    setDeliveryMethod(null);
    setBlockedExpanded(false);
    setUseCustomEffectiveDate(false);
    setEffectiveDate(todayDateInputValue());
    setAssignError(null);
    setBulkResult(null);
  };

  useEffect(() => {
    if (!open) return;
    const ids = [...dpIds];
    setResolvedDpIds(ids);
    resetForm();

    const load = async () => {
      const dpErr = validateBulkDpCount(ids.length);
      if (dpErr) {
        setPreviewError(dpErr);
        return;
      }

      setLoadingPreview(true);
      setLoadingNotices(true);
      try {
        const clientId = user?.clientId;
        const [previewData, noticesResp, settings] = await Promise.all([
          noticesService.bulkAssignPreview(ids),
          noticesService.getNoticesList(
            { status: 'ACTIVE', includeConsentNotices: false },
            { page: 1, pageSize: 100 },
            'newest'
          ),
          clientId ? getClientSettings(clientId) : Promise.resolve(null),
        ]);

        setPreview(previewData);
        const noticeItems: {
          noticeId: number;
          noticeType: NoticeType;
          title: string;
          description?: string | null;
        }[] = [];
        for (const n of noticesResp.notices || []) {
          if (n.NoticeId == null || !n.NoticeType) continue;
          noticeItems.push({
            noticeId: n.NoticeId,
            noticeType: n.NoticeType,
            title: n.Title || 'Untitled Notice',
            description: n.Description ?? null,
          });
        }
        setNotices(noticeItems);

        const sms = parseEnabled(settings?.PHONE_NOTIFICATION_ENABLE_SMS);
        const wa = parseEnabled(settings?.PHONE_NOTIFICATION_ENABLE_WHATSAPP);
        setSmsEnabled(sms);
        setWhatsAppEnabled(wa);

        if (previewData.phoneCount > 0) {
          if (sms && wa) setDeliveryMethod('BOTH');
          else if (sms) setDeliveryMethod('SMS');
          else if (wa) setDeliveryMethod('WHATSAPP');
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Failed to load bulk assign data';
        setPreviewError(msg);
      } finally {
        setLoadingPreview(false);
        setLoadingNotices(false);
      }
    };

    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once per open; ids captured at open
  }, [open, user?.clientId]);

  const filteredNotices = useMemo(() => {
    const s = searchTerm.trim().toLowerCase();
    return notices
      .filter((n) => typeFilter === 'ALL' || n.noticeType === typeFilter)
      .filter((n) => {
        if (!s) return true;
        return `${n.title} ${n.description || ''}`.toLowerCase().includes(s);
      })
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [notices, searchTerm, typeFilter]);

  const selectedNoticeCount = selectedNoticeIds.size;
  const phoneCount = preview?.phoneCount ?? 0;
  const assignableCount = (preview?.emailCount ?? 0) + phoneCount;

  const billableUnits = useMemo(() => {
    if (!deliveryMethod || phoneCount === 0 || selectedNoticeCount === 0) return 0;
    return computeBillableUnits(phoneCount, selectedNoticeCount, deliveryMethod);
  }, [phoneCount, selectedNoticeCount, deliveryMethod]);

  const billableError = useMemo(
    () => validateBulkBillableUnits(phoneCount, selectedNoticeCount, deliveryMethod ?? undefined),
    [phoneCount, selectedNoticeCount, deliveryMethod]
  );

  const toggleNotice = (noticeId: number) => {
    setSelectedNoticeIds((prev) => {
      const next = new Set(prev);
      if (next.has(noticeId)) next.delete(noticeId);
      else next.add(noticeId);
      return next;
    });
  };

  const handleAssign = async () => {
    setAssignError(null);
    if (selectedNoticeCount === 0) {
      setAssignError('Select at least one notice.');
      return;
    }
    if (assignableCount === 0) {
      setAssignError('No assignable recipients (all selected DPs are blocked).');
      return;
    }
    if (billableError) {
      setAssignError(billableError);
      return;
    }
    if (phoneCount > 0 && (smsEnabled || whatsAppEnabled) && !deliveryMethod) {
      setAssignError('Select a phone delivery method.');
      return;
    }

    try {
      setAssigning(true);
      const result = await noticesService.bulkAssignNotices({
        noticeIds: Array.from(selectedNoticeIds),
        dpIds: resolvedDpIds,
        ...(phoneCount > 0 && deliveryMethod ? { deliveryMethod } : {}),
        ...(useCustomEffectiveDate
          ? { effectiveDate: dateInputToIsoStartOfDay(effectiveDate) }
          : {}),
      });
      setBulkResult(result);
      setStep('results');
    } catch (e: unknown) {
      setAssignError(getApiErrorMessage(e, 'Bulk assign failed'));
    } finally {
      setAssigning(false);
    }
  };

  const handleClose = () => {
    if (assigning) return;
    onClose();
  };

  const handleDone = () => {
    onSuccess();
    onClose();
  };

  const failedResults = bulkResult?.results.filter((r) => !r.success) ?? [];
  const loading = loadingPreview || loadingNotices;

  const assignDisabled =
    assigning ||
    loading ||
    !!previewError ||
    selectedNoticeCount === 0 ||
    assignableCount === 0 ||
    !!billableError ||
    (phoneCount > 0 && (smsEnabled || whatsAppEnabled) && !deliveryMethod);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Box>
            <Typography variant="h6">Bulk assign notice ({resolvedDpIds.length || dpIds.length})</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Breach, erasure, and informational notices only. Consent notices use Send Consent Notice on the list.
            </Typography>
          </Box>
          <IconButton onClick={handleClose} disabled={assigning} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {step === 'form' && (
          <>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress />
              </Box>
            )}

            {previewError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {previewError}
              </Alert>
            )}

            {!loading && preview && (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Recipients
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                  <Chip icon={<EmailIcon />} label={`Email: ${preview.emailCount}`} size="small" variant="outlined" />
                  <Chip icon={<PhoneIcon />} label={`Phone: ${preview.phoneCount}`} size="small" variant="outlined" />
                  {preview.blockedCount > 0 && (
                    <Chip
                      icon={<BlockIcon />}
                      label={`Blocked: ${preview.blockedCount}`}
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Email DPs receive email only. SMS / WhatsApp apply to phone (or guardian phone for minors) recipients
                  only.
                </Typography>

                {preview.blockedCount > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Button
                      size="small"
                      startIcon={blockedExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      onClick={() => setBlockedExpanded((v) => !v)}
                    >
                      {blockedExpanded ? 'Hide' : 'Show'} blocked DPs ({preview.blockedCount})
                    </Button>
                    <Collapse in={blockedExpanded}>
                      <Box sx={{ mt: 1, maxHeight: 120, overflow: 'auto' }}>
                        {preview.blocked.map((b) => (
                          <Typography key={b.dpId} variant="caption" display="block" color="text.secondary">
                            DP #{b.dpId}: {b.reason}
                          </Typography>
                        ))}
                      </Box>
                    </Collapse>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Notices
                </Typography>

                {noticesError && (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    {noticesError}
                  </Alert>
                )}

                <TextField
                  fullWidth
                  size="small"
                  label="Search notices"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{ mb: 1 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                  {(['ALL', 'BREACH_NOTICE', 'ERASURE_NOTICE', 'INFORMATIONAL_NOTICE'] as NoticeTypeFilter[]).map(
                    (t) => (
                      <Chip
                        key={t}
                        size="small"
                        label={t === 'ALL' ? 'All' : noticeTypeLabel(t as NoticeType)}
                        onClick={() => setTypeFilter(t)}
                        color={typeFilter === t ? 'primary' : 'default'}
                        variant={typeFilter === t ? 'filled' : 'outlined'}
                      />
                    )
                  )}
                </Box>

                <Box
                  sx={{
                    maxHeight: 220,
                    overflow: 'auto',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    p: 1,
                  }}
                >
                  {filteredNotices.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                      No notices match filters.
                    </Typography>
                  ) : (
                    filteredNotices.map((n) => (
                      <FormControlLabel
                        key={n.noticeId}
                        control={
                          <Checkbox
                            checked={selectedNoticeIds.has(n.noticeId)}
                            onChange={() => toggleNotice(n.noticeId)}
                          />
                        }
                        label={
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Chip size="small" label={noticeTypeLabel(n.noticeType)} />
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {n.title}
                              </Typography>
                            </Box>
                          </Box>
                        }
                        sx={{ display: 'flex', alignItems: 'flex-start', m: 0, py: 0.5 }}
                      />
                    ))
                  )}
                </Box>

                {phoneCount > 0 && (smsEnabled || whatsAppEnabled) && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Phone delivery
                    </Typography>
                    <FormControl>
                      <RadioGroup
                        row
                        value={deliveryMethod || ''}
                        onChange={(e) => setDeliveryMethod(e.target.value as BulkPhoneDeliveryMethod)}
                      >
                        {smsEnabled && <FormControlLabel value="SMS" control={<Radio />} label="SMS" />}
                        {whatsAppEnabled && (
                          <FormControlLabel value="WHATSAPP" control={<Radio />} label="WhatsApp" />
                        )}
                        {smsEnabled && whatsAppEnabled && (
                          <FormControlLabel value="BOTH" control={<Radio />} label="Both" />
                        )}
                      </RadioGroup>
                    </FormControl>
                    {selectedNoticeCount > 0 && deliveryMethod && (
                      <Alert
                        severity={billableUnits > MAX_BULK_PHONE_BILLABLE_UNITS ? 'warning' : 'info'}
                        sx={{ mt: 1 }}
                      >
                        {formatBillableUnitsEstimate(phoneCount, selectedNoticeCount, deliveryMethod)}
                      </Alert>
                    )}
                  </>
                )}

                {phoneCount > 0 && !smsEnabled && !whatsAppEnabled && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    Phone recipients are included, but SMS and WhatsApp are disabled in client settings. They will only
                    receive email if applicable.
                  </Alert>
                )}

                <EffectiveDateSection
                  noticeHelpVariant="BULK_MIXED"
                  useCustomEffectiveDate={useCustomEffectiveDate}
                  onUseCustomEffectiveDateChange={setUseCustomEffectiveDate}
                  effectiveDate={effectiveDate}
                  onEffectiveDateChange={setEffectiveDate}
                />
              </>
            )}

            {assignError && (
              <Alert severity="error" sx={{ mt: 2 }} onClose={() => setAssignError(null)}>
                {assignError}
              </Alert>
            )}
          </>
        )}

        {step === 'results' && bulkResult && (
          <Box>
            <Alert severity={bulkResult.failed === 0 ? 'success' : 'warning'} sx={{ mb: 2 }}>
              {bulkResult.successful} succeeded, {bulkResult.failed} failed (of {bulkResult.totalRequested} requested).
            </Alert>
            {failedResults.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Failures
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>DP</TableCell>
                      <TableCell>Notice</TableCell>
                      <TableCell>Channel</TableCell>
                      <TableCell>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {failedResults.map((r, idx) => (
                      <TableRow key={`${r.dpId}-${r.noticeId}-${idx}`}>
                        <TableCell>{r.dpId}</TableCell>
                        <TableCell>{r.noticeId}</TableCell>
                        <TableCell>{r.channel || '—'}</TableCell>
                        <TableCell>{r.error || 'Unknown'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {step === 'form' ? (
          <>
            <Button onClick={handleClose} disabled={assigning}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => void handleAssign()}
              disabled={assignDisabled}
              startIcon={assigning ? <CircularProgress size={16} /> : null}
            >
              {assigning ? 'Assigning…' : 'Assign'}
            </Button>
          </>
        ) : (
          <Button variant="contained" onClick={handleDone}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkAssignNoticeModal;
